import { usePropertyContext } from '@/context/propertyContext';
import { dataDate } from '@/features/dates/date-helpers';
import type {
	GroupPriceToolInput,
	NetBenefitData,
} from '@/features/group-pricing/group-pricing-utils';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
	calculateCompRoomCost,
	calculateCostOfGuestsWalked,
	calculateDisplacementCost,
	calculateFBMeetingBenefit,
	calculateForecastADR,
	calculateGuestsWalked,
	calculateIncrementalCommission,
	calculateIncrementalRoomRevenue,
	calculateNetBenefit,
	calculateNetRoomsAdded,
	calculateOverride,
	calculatePlannersAdvCost,
	calculateRebateCost,
	calculateRequiredProfit,
	calculateRoomsDisplaced,
	calculateShoulderDisplacementCost,
	calculateShoulderRoomsDisplaced,
	calculateTotalOnTheBooks,
	calculateVariableOpEx,
} from '../calculations';
import { getPreviousDayUTC } from '../date-utils';
import { useGetNetBenefitQuery } from '../gql/_gen_/group-pricing.gql';

export interface ProcessedNetBenefitEntry extends NetBenefitData {
	forecast_transient_adr: number;
	total_fcst: number;
	new_group_rooms_requested: number;
	net_rooms_added: number;
	rooms_displaced: number;
	guests_walked: number;
	shoulder_rooms_displaced: number;
	transient_and_group_fcst: number;
	total_on_the_books: number; // New field for total inventory on the books
	incremental_room_revenue: number;
	cost_of_guests_walked: number;
	incremental_direct_variable_opex: number;
	incremental_commission: number;
	comp_room_cost: number;
	rebate_cost: number;
	displacement_cost: number;
	shoulder_displacement_cost: number;
	combined_f_and_b_meeting_space_benefit: number;
	planners_advantage_points_cost: number;
	net_benefit: number;
	required_profit: number;
	meeting_space_benefit: number;
}

export interface DateValueData {
	value: string;
	isNegative?: boolean;
}

export interface DateValues {
	[date: string]: DateValueData;
}

export interface NetBenefitRow {
	key?: string;
	/** Display label for the row (e.g., "Capacity", "Net Benefit") */
	label: string;
	/** Formatted total value combining all dates (e.g., "194", "$200.00") */
	total: string;
	/** Map of date strings to formatted values */
	dateValues: DateValues;
	/** Whether the value is negative (affects styling) */
	isNegative?: boolean;
	/** Whether the row should be highlighted (affects styling) */
	isHighlighted?: boolean;
}

export const useNetBenefitData = () => {
	const form = useFormContext();

	const formValues = useWatch({
		control: form.control,
	}) as GroupPriceToolInput;

	const [checkIn, checkOut] = useWatch({
		control: form.control,
		name: ['check_in', 'check_out'],
	});

	const { property } = usePropertyContext();

	const { data, loading, error } = useGetNetBenefitQuery({
		skip: !property?.propertyId,
		variables: {
			startDate: checkIn,
			endDate: getPreviousDayUTC(checkOut),
			propertyId: property?.propertyId,
		},
	});

	const adjustNumberValue = (value: unknown): number => {
		let numValue = value;

		if (typeof value === 'string') {
			numValue = Number.parseFloat(value);
		} else if (typeof value === 'number') {
			numValue = value;
		} else if (
			typeof value === 'object' &&
			value !== null &&
			'value' in value
		) {
			numValue = (value as { value: number }).value;
		}

		return numValue as number;
	};

	// Helper functions for formatting
	const formatNumberToString = (value: number): string => {
		const numValue = adjustNumberValue(value);

		if (Number.isNaN(numValue) || numValue === 0) return '-';

		return Math.round(numValue).toString();
	};

	const formatCurrency = (value: unknown): string => {
		const numValue = adjustNumberValue(value);

		if (Number.isNaN(numValue) || numValue === 0) return '-';

		// To prevent confusion about rounding, we use Math.floor and no decimal places
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(Math.floor(numValue as number));
	};

	// Calculate raw data entries whenever form values or raw data changes
	const rawCalculatedData = useMemo(() => {
		if (!data?.getNetBenefit || !formValues) return [];

		// Safety check for all form values
		const {
			rooms_requested = 0,
			comp_rooms = 0,
			rate_requested = 0,
			rebate = 0,
			planners_adv_points = 0,
			direct_var_opex = 0,
			occ_fcst_override = 0,
			adr_fcst_override = 0,
			wash_factor = 0,
			commission = 0,
			profit_margin = 0,
			f_and_b_profit = 0,
			avg_stay_length = 0,
			meeting_space_benefit = 0,
		} = formValues;

		// Get all dates for shoulder room calculations
		const allDates = data.getNetBenefit
			.filter((entry): entry is NonNullable<typeof entry> => entry != null)
			.map((entry) => entry.stay_date || '')
			.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

		const firstDate = allDates.length > 0 ? allDates[0] : '';
		const lastDate = allDates.length > 0 ? allDates[allDates.length - 1] : '';
		const numberOfNights = allDates.length;

		return data.getNetBenefit
			.filter(
				(dataEntry): dataEntry is NonNullable<typeof dataEntry> =>
					dataEntry != null,
			)
			.map((dataEntry) => {
				// Safety check for all data entry values
				const {
					current_otb = 0,
					total_capacity = 0,
					total_fcst = 0,
					group_booked = 0,
					group_blocked = 0,
					fcst_rev = 0,
					stay_date = '',
				} = dataEntry;

				// Calculate forecast ADR with override if provided
				const forecast_transient_adr = calculateForecastADR(
					fcst_rev,
					total_fcst,
					adr_fcst_override,
				);

				// Parse rooms requested to ensure it's a number
				const new_group_rooms_requested =
					typeof rooms_requested === 'string'
						? Number.parseInt(rooms_requested)
						: rooms_requested;

				// Calculate total forecast with override if provided
				const total_fcst_override =
					occ_fcst_override !== 0
						? calculateOverride(total_fcst, occ_fcst_override)
						: total_fcst;

				// Calculate total forecast including existing group bookings
				const transient_and_group_fcst = total_fcst_override + group_booked;

				// Calculate displaced rooms
				const rooms_displaced = calculateRoomsDisplaced(
					total_capacity,
					new_group_rooms_requested,
					transient_and_group_fcst,
				);

				// Calculate net rooms added after accounting for washed rooms and displacement
				const net_rooms_added = calculateNetRoomsAdded(
					total_capacity,
					new_group_rooms_requested,
					transient_and_group_fcst,
					wash_factor,
				);

				// Calculate guests walked if OTB exceeds capacity
				const guests_walked = calculateGuestsWalked(
					total_capacity,
					new_group_rooms_requested,
					current_otb,
				);

				// Calculate shoulder rooms displaced
				const shoulder_rooms_displaced = calculateShoulderRoomsDisplaced(
					rooms_displaced.value,
					avg_stay_length,
					firstDate,
					lastDate,
					stay_date,
				);

				// Calculate shoulder displacement cost
				const shoulder_displacement_cost = calculateShoulderDisplacementCost(
					shoulder_rooms_displaced,
					forecast_transient_adr,
					rate_requested,
				);

				// Calculate total on the books
				const total_on_the_books = calculateTotalOnTheBooks(
					current_otb,
					group_booked,
					group_blocked,
				);

				// Calculate incremental room revenue
				const incremental_room_revenue = calculateIncrementalRoomRevenue(
					net_rooms_added,
					rate_requested,
				);

				// Calculate cost of walked guests
				const cost_of_guests_walked = calculateCostOfGuestsWalked(
					guests_walked,
					forecast_transient_adr,
				);

				// Calculate incremental direct variable operating expenses
				const incremental_direct_variable_opex = calculateVariableOpEx(
					net_rooms_added,
					direct_var_opex,
				);

				// Calculate incremental commission
				const incremental_commission = calculateIncrementalCommission(
					rooms_displaced.value,
					forecast_transient_adr,
					commission,
					rate_requested,
					total_capacity,
					total_fcst,
					rooms_requested,
				);

				// Calculate comp room cost
				const comp_room_cost = calculateCompRoomCost(
					comp_rooms,
					rate_requested,
				);

				// Calculate rebate cost
				const rebate_cost = calculateRebateCost(rebate, rooms_requested);

				// Calculate displacement cost
				const displacement_cost = calculateDisplacementCost(
					rooms_displaced.value,
					forecast_transient_adr,
					rate_requested,
				);

				// Calculate combined F&B and meeting space benefit
				const combined_f_and_b_meeting_space_benefit =
					calculateFBMeetingBenefit(
						f_and_b_profit,
						net_rooms_added,
						meeting_space_benefit,
						numberOfNights,
					);

				// Calculate planner's advantage points cost
				const planners_advantage_points_cost = calculatePlannersAdvCost(
					planners_adv_points,
					rate_requested,
					rooms_requested,
				);

				// Calculate net benefit
				const net_benefit = calculateNetBenefit(
					incremental_room_revenue.value,
					cost_of_guests_walked.value,
					comp_room_cost.value,
					rebate_cost.value,
					displacement_cost.value,
					incremental_direct_variable_opex.value,
					incremental_commission.value,
					combined_f_and_b_meeting_space_benefit.value,
					planners_advantage_points_cost.value,
				);

				// Calculate required profit
				const required_profit = calculateRequiredProfit(
					rooms_requested,
					rate_requested,
					profit_margin,
				);

				// Return processed data entry with all calculated fields
				return {
					...dataEntry,
					forecast_transient_adr,
					total_fcst: total_fcst_override,
					new_group_rooms_requested,
					net_rooms_added,
					rooms_displaced,
					guests_walked,
					shoulder_rooms_displaced,
					transient_and_group_fcst,
					total_on_the_books,
					incremental_room_revenue,
					cost_of_guests_walked,
					incremental_direct_variable_opex,
					incremental_commission,
					comp_room_cost,
					rebate_cost,
					displacement_cost,
					shoulder_displacement_cost,
					combined_f_and_b_meeting_space_benefit,
					planners_advantage_points_cost,
					net_benefit,
					required_profit,
					total_revenue: rate_requested * new_group_rooms_requested,
				};
			});
	}, [data?.getNetBenefit, formValues]);

	// Calculate total net benefit and required profit from rawCalculatedData
	// This is used for both the table display and the group result decision
	const calculatedTotals = useMemo(() => {
		if (!rawCalculatedData.length) {
			return {
				totalNetBenefit: 0,
				totalRequiredProfit: 0,
			};
		}

		// Calculate total net benefit across all days
		const totalNetBenefit = rawCalculatedData.reduce(
			(sum, entry) => sum + (entry.net_benefit.value || 0),
			0,
		);

		// Calculate total required profit across all days
		const totalRequiredProfit = rawCalculatedData.reduce(
			(sum, entry) => sum + (entry.required_profit.value || 0),
			0,
		);

		const totalRevenue = rawCalculatedData.reduce(
			(sum, entry) => sum + (entry.total_revenue || 0),
			0,
		);

		return {
			totalNetBenefit,
			totalRequiredProfit,
			totalRevenue: totalRevenue || 0,
			takeGroup: totalNetBenefit > totalRequiredProfit,
		};
	}, [rawCalculatedData]);

	// Transform the raw calculated data into the desired format
	const processedData = useMemo(() => {
		if (!rawCalculatedData.length) return [];

		// Field mappings with their display labels
		const fieldMappings: {
			key: string;
			label: string;
			isCurrency: boolean;
			isHighlighted?: boolean;
		}[] = [
			{ key: 'total_capacity', label: 'Capacity', isCurrency: false },
			{ key: 'total_on_the_books', label: 'On the Books', isCurrency: false },
			{
				key: 'forecast_transient_adr',
				label: 'Forecast Transient ADR',
				isCurrency: true,
			},
			{ key: 'total_fcst', label: 'Transient Forecast', isCurrency: false },
			{
				key: 'transient_and_group_fcst',
				label: 'Transient and Group Forecast',
				isCurrency: false,
			},
			{
				key: 'new_group_rooms_requested',
				label: 'New Group Rooms Requested',
				isCurrency: false,
			},
			{ key: 'net_rooms_added', label: 'Net Rooms Added', isCurrency: false },
			{ key: 'rooms_displaced', label: 'Rooms Displaced', isCurrency: false },
			{ key: 'guests_walked', label: 'Guests Walked', isCurrency: false },
			{
				key: 'shoulder_rooms_displaced',
				label: 'Shoulder Rooms Displaced',
				isCurrency: false,
			},
			{
				key: 'incremental_room_revenue',
				label: 'Incremental Room Revenue',
				isCurrency: true,
			},
			{
				key: 'cost_of_guests_walked',
				label: 'Cost of Guests Walked',
				isCurrency: true,
			},
			{
				key: 'incremental_direct_variable_opex',
				label: 'Incremental Direct Variable OPEX',
				isCurrency: true,
			},
			{
				key: 'incremental_commission',
				label: 'Incremental Commission',
				isCurrency: true,
			},
			{ key: 'comp_room_cost', label: 'Comp Room Cost', isCurrency: true },
			{ key: 'rebate_cost', label: 'Rebate Cost', isCurrency: true },
			{
				key: 'displacement_cost',
				label: 'Displacement Cost',
				isCurrency: true,
			},
			{
				key: 'shoulder_displacement_cost',
				label: 'Shoulder Displacement Cost',
				isCurrency: true,
			},
			{
				key: 'combined_f_and_b_meeting_space_benefit',
				label: 'Combined F&B, Meeting Space Benefit',
				isCurrency: true,
			},
			{
				key: 'planners_advantage_points_cost',
				label: 'Planners Advantage Points Cost',
				isCurrency: true,
			},
			{
				key: 'net_benefit',
				label: 'Net Benefit',
				isCurrency: true,
				isHighlighted: true,
			},
			{
				key: 'required_profit',
				label: 'Required Profit',
				isCurrency: true,
				isHighlighted: true,
			},
		];

		// Create transformed data
		return fieldMappings.map((field) => {
			const row: NetBenefitRow = {
				key: field.key,
				label: field.label,
				dateValues: {},
				total: '',
			};

			// Get values for each date
			rawCalculatedData.forEach((entry) => {
				const value = (entry as any)[field.key];
				const formattedValue = field.isCurrency
					? formatCurrency(value)
					: formatNumberToString(value);

				if (!entry.stay_date) return;

				const valueIsNegative =
					(typeof value === 'object' && value?.value < 0) ||
					(typeof value === 'number' && value < 0);

				row.dateValues[dataDate(entry.stay_date)] = {
					value: formattedValue,
					isNegative: valueIsNegative,
				};
			});

			// Calculate the total value
			if (field.key === 'forecast_transient_adr') {
				// We must add up forecasted revenue and divide by the sum of forecasted rooms
				const totalFcstRevenue = rawCalculatedData.reduce((sum, entry) => {
					if (!entry.fcst_rev) {
						entry.fcst_rev = 0;
					}

					if (entry.fcst_rev === 0) return sum;

					return sum + entry.fcst_rev;
				}, 0);

				const totalFcstRooms = rawCalculatedData.reduce((sum, entry) => {
					if (!entry.total_fcst) {
						entry.total_fcst = 0;
					}

					if (entry.total_fcst === 0) return sum;

					return sum + entry.total_fcst;
				}, 0);
				const average =
					totalFcstRooms > 0 ? totalFcstRevenue / totalFcstRooms : 0;
				if (average < 0) {
					row.isNegative = true;
				}
				row.total = formatCurrency(average);
			} else if (field.key === 'net_benefit') {
				// Use the calculated total net benefit from calculatedTotals
				const total = calculatedTotals.totalNetBenefit;
				row.total = formatCurrency(total);
				if (total < 0) {
					row.isNegative = true;
				}
			} else if (field.key === 'required_profit') {
				// Use the calculated total required profit from calculatedTotals
				const total = calculatedTotals.totalRequiredProfit;
				row.total = formatCurrency(total);
				if (total < 0) {
					row.isNegative = true;
				}
			} else if (field.isCurrency) {
				// For currency fields, sum the numeric values and format the result
				const total = rawCalculatedData.reduce((sum, entry) => {
					if (field.key === 'incremental_commission') {
						return sum + ((entry as any)[field.key]?.value || 0);
					}
					return sum + ((entry as any)[field.key] || 0);
				}, 0);
				row.total = formatCurrency(total);
				if (total < 0) {
					row.isNegative = true;
				}
			} else {
				// For non-currency fields, just sum the values
				const total = rawCalculatedData.reduce((sum, entry) => {
					if (field.key === 'incremental_commission') {
						return sum + ((entry as any)[field.key]?.value || 0);
					}
					return sum + ((entry as any)[field.key] || 0);
				}, 0);
				row.total = formatNumberToString(total);
				if (total < 0) {
					row.isNegative = true;
				}
			}

			// Add highlighting if specified
			if (field.isHighlighted) {
				row.isHighlighted = true;
			}

			return row;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawCalculatedData, calculatedTotals]);

	const actualProfitMargin = Math.floor(
		(calculatedTotals.totalNetBenefit / calculatedTotals.totalRevenue! || 0) *
			100,
	);

	// Return both raw and processed data
	return {
		rawData: data,
		rawCalculatedData,
		processedData: processedData as NetBenefitRow[],
		loading,
		error,
		checkIn,
		checkOut,
		groupResult: {
			takeGroup: calculatedTotals.takeGroup,
			netBenefit: calculatedTotals.totalNetBenefit,
			requiredProfitMargin: formValues.profit_margin,
			actualProfitMargin,
			requiredProfit: calculatedTotals.totalRequiredProfit,
		},
	};
};
